// src/pages/agenda/ConfigLembrete.js — "Lembrete ao cliente" (Agenda, fase 2).
//
// Painel à direita (tela inteira no celular), mesma linguagem do painel de
// compromisso. Só supervisor abre — o servidor confere de novo.
//
// O que a empresa escolhe (decisões de out/26):
//   - o template aprovado e o chip da conta Meta onde ele existe;
//   - de onde vem cada variável do template — com uma prévia que mostra a
//     mensagem como o cliente vai ler;
//   - quando sai: até 3 antecedências de uma lista fechada;
//   - se o template tiver botões de resposta, qual CONFIRMA a consulta e
//     qual pede pra REMARCAR (esse só avisa o responsável).
import { useEffect, useMemo, useState } from 'react';
import { Box, Drawer } from '@mui/material';
import { FiX } from 'react-icons/fi';
import api from '../../services/Api';
import { FONT_TXT, botaoBase, rotuloSecao, umTipo } from './estilo';

const toque = { '@media (pointer: coarse)': { minHeight: 40 } };
const campo = {
  height: 40, boxSizing: 'border-box', width: '100%', bgcolor: 'var(--ch-inset)', border: '1px solid var(--ch-bdr2)',
  borderRadius: '8px', px: '8px', color: 'var(--ch-text)', fontFamily: FONT_TXT, fontSize: '16px', outline: 'none',
  colorScheme: 'light dark', '&:focus': { borderColor: 'var(--ch-green)' }, '&::placeholder': { color: 'var(--ch-text3)' },
};
const titulo = { ...rotuloSecao, display: 'block', mb: '6px', letterSpacing: '0.14em' };
const ajuda = { fontSize: '0.68rem', color: 'var(--ch-text2)', lineHeight: 1.5 };

function Interruptor({ ligado, onClick, children, descricao }) {
  return (
    <Box component="button" type="button" role="switch" aria-checked={ligado} onClick={onClick}
      sx={{ ...botaoBase, ...toque, display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%', textAlign: 'left', p: '12px',
        borderRadius: '8px', border: '1px solid var(--ch-bdr)', bgcolor: ligado ? 'var(--ch-green-bg)' : 'transparent', color: 'var(--ch-text)' }}>
      <Box component="span" aria-hidden sx={{ flex: 'none', mt: '1px', width: 30, height: 18, borderRadius: 9, position: 'relative',
        bgcolor: ligado ? 'var(--ch-green)' : 'var(--ch-bdr2)', transition: 'background-color .15s' }}>
        <Box component="span" sx={{ position: 'absolute', top: 2, left: ligado ? 14 : 2, width: 14, height: 14, borderRadius: '50%', bgcolor: 'var(--ch-card)', transition: 'left .15s' }} />
      </Box>
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{children}</Box>
        {descricao && <Box component="span" sx={ajuda}>{descricao}</Box>}
      </Box>
    </Box>
  );
}

// Texto do corpo do template com os {{n}} trocados pelos exemplos.
function previa(componentes, variaveis, exemplos) {
  const corpo = (componentes || []).find((c) => (c.type || '').toUpperCase() === 'BODY');
  if (!corpo?.text) return '';
  return corpo.text.replace(/\{\{\s*(\d+)\s*\}\}/g, (m, n) => {
    const v = variaveis[`BODY:${n}`];
    if (!v?.fonte) return m;
    if (v.fonte === 'fixo') return v.valor || m;
    return exemplos?.[v.fonte] || m;
  });
}

export default function ConfigLembrete({ aberto, nome, onFechar, onSalvo }) {
  const [base, setBase] = useState(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [devices, setDevices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [carregandoTpl, setCarregandoTpl] = useState(false);
  const [ativo, setAtivo] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [tplChave, setTplChave] = useState('');
  const [campos, setCampos] = useState({ slots: [], botoes: [] });
  const [variaveis, setVariaveis] = useState({});
  const [antecedencias, setAntecedencias] = useState([1440]);
  const [confirmar, setConfirmar] = useState('');
  const [remarcar, setRemarcar] = useState('');

  // Carrega a configuração salva e os chips ao abrir.
  useEffect(() => {
    if (!aberto) return;
    setErro(''); setBase(null);
    api.get('/agenda/lembrete-cliente').then(({ data }) => {
      setBase(data);
      setAtivo(!!data.ativo);
      setDeviceId(data.device_id || '');
      setTplChave(data.template ? `${data.template}|${data.idioma}` : '');
      setVariaveis(data.variaveis || {});
      setAntecedencias(data.antecedencias?.length ? data.antecedencias : [1440]);
      setConfirmar(data.botao_confirmar || '');
      setRemarcar(data.botao_remarcar || '');
      setCampos({ slots: data.slots || [], botoes: data.botoes || [] });
    }).catch((err) => setErro(err?.response?.data?.detail || 'Não foi possível abrir a configuração.'));
    api.get('/templates/meta-info').then(({ data }) => {
      const ds = data?.devices || [];
      setDevices(ds);
      setDeviceId((d) => d || (ds.length === 1 ? ds[0].id : ''));
    }).catch(() => setDevices([]));
  }, [aberto]);

  // Templates aprovados do chip escolhido.
  useEffect(() => {
    if (!aberto || !deviceId) { setTemplates([]); return; }
    setCarregandoTpl(true);
    api.get(`/templates/list/${deviceId}`)
      .then(({ data }) => setTemplates((data?.templates || []).filter((t) => (t.status || '').toUpperCase() === 'APPROVED')))
      .catch(() => setTemplates([]))
      .finally(() => setCarregandoTpl(false));
  }, [aberto, deviceId]);

  const template = useMemo(() => templates.find((t) => `${t.name}|${t.language}` === tplChave) || null, [templates, tplChave]);

  // Variáveis e botões do template escolhido (o servidor conta).
  useEffect(() => {
    if (!template) return;
    api.post('/agenda/lembrete-cliente/campos', { components: template.components || [] })
      .then(({ data }) => {
        setCampos(data);
        // Mantém o que já estava escolhido pros slots que continuam existindo.
        setVariaveis((v) => Object.fromEntries((data.slots || []).map((s) => [s.slot, v[s.slot] || {}])));
        setConfirmar((b) => ((data.botoes || []).includes(b) ? b : ''));
        setRemarcar((b) => ((data.botoes || []).includes(b) ? b : ''));
      })
      .catch(() => setCampos({ slots: [], botoes: [] }));
  }, [template]);

  const trocarAntecedencia = (m) => setAntecedencias((a) => {
    if (a.includes(m)) return a.filter((x) => x !== m);
    if (a.length >= (base?.max_antecedencias || 3)) return a;
    return [...a, m];
  });

  const salvar = async () => {
    if (salvando) return;
    setErro('');
    if (ativo) {
      if (!deviceId) { setErro('Escolha o chip.'); return; }
      if (!template) { setErro('Escolha o template.'); return; }
      const faltando = campos.slots.find((s) => !variaveis[s.slot]?.fonte || (variaveis[s.slot].fonte === 'fixo' && !variaveis[s.slot].valor?.trim()));
      if (faltando) { setErro(`Escolha de onde vem ${faltando.rotulo}.`); return; }
      if (!antecedencias.length) { setErro('Escolha quando o lembrete sai.'); return; }
    }
    setSalvando(true);
    try {
      await api.put('/agenda/lembrete-cliente', ativo ? {
        ativo: true, device_id: deviceId, template: template.name, idioma: template.language,
        variaveis, antecedencias, botao_confirmar: confirmar || null, botao_remarcar: remarcar || null,
      } : { ativo: false });
      await onSalvo?.();
      onFechar?.();
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível salvar. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  const consulta = umTipo('consulta', nome).toLowerCase();
  const textoPrevia = template ? previa(template.components, variaveis, base?.exemplos) : '';

  return (
    <Drawer anchor="right" open={aberto} onClose={() => !salvando && onFechar?.()}
      PaperProps={{
        'data-screen-label': 'Lembrete ao cliente',
        sx: { width: { xs: '100%', sm: 440 }, boxSizing: 'border-box', bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', fontFamily: FONT_TXT,
          borderLeft: '1px solid var(--ch-bdr2)', boxShadow: '-18px 0 48px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' },
      }}>
      <Box sx={{ p: '14px 20px', borderBottom: '1px solid var(--ch-bdr)', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--ch-text3)', textTransform: 'uppercase' }}>Agenda</Box>
          <Box component="h2" sx={{ m: 0, fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.4 }}>Lembrete ao cliente</Box>
        </Box>
        <Box component="button" type="button" aria-label="Fechar" onClick={() => !salvando && onFechar?.()}
          sx={{ ...botaoBase, width: 30, height: 30, '@media (pointer: coarse)': { width: 40, height: 40 }, borderRadius: '7px', border: '1px solid var(--ch-bdr)', bgcolor: 'transparent', color: 'var(--ch-text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0, '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' } }}>
          <FiX size={15} />
        </Box>
      </Box>

      {/* Os blocos não encolhem: numa coluna flex que rola, o navegador
          achatava o interruptor e o texto dele vazava por cima do campo de
          baixo (visto em 375px). */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 20px', display: 'flex', flexDirection: 'column', gap: '18px', '& > *': { flexShrink: 0 } }}>
        {!base && !erro && <Box sx={ajuda}>Carregando…</Box>}
        {base && (
          <Interruptor ligado={ativo} onClick={() => setAtivo((v) => !v)}
            descricao={`O cliente recebe um template no WhatsApp antes da ${consulta}. Não conta como campanha: o teto de custo não bloqueia.`}>
            Enviar lembrete automático
          </Interruptor>
        )}

        {base && ativo && (
          <>
            {devices.length > 1 && (
              <Box>
                <Box component="label" htmlFor="lc-chip" sx={titulo}>Chip que envia</Box>
                <Box component="select" id="lc-chip" value={deviceId} onChange={(e) => { setDeviceId(e.target.value); setTplChave(''); }} sx={campo}>
                  <option value="">Escolha o chip</option>
                  {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Box>
                <Box sx={{ ...ajuda, mt: '4px' }}>Se o contato já conversa por outro chip da mesma conta Meta, o lembrete sai por ele.</Box>
              </Box>
            )}

            <Box>
              <Box component="label" htmlFor="lc-tpl" sx={titulo}>Template</Box>
              <Box component="select" id="lc-tpl" value={tplChave} onChange={(e) => setTplChave(e.target.value)} disabled={!deviceId || carregandoTpl} sx={campo}>
                <option value="">{carregandoTpl ? 'Carregando templates…' : 'Escolha um template aprovado'}</option>
                {templates.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.label || t.name} ({t.language})</option>)}
              </Box>
              {deviceId && !carregandoTpl && templates.length === 0 && (
                <Box sx={{ ...ajuda, mt: '4px' }}>Nenhum template aprovado neste chip. Crie um na tela de Templates.</Box>
              )}
            </Box>

            {template && campos.slots.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box component="span" sx={titulo}>Variáveis</Box>
                {campos.slots.map((s) => {
                  const v = variaveis[s.slot] || {};
                  return (
                    <Box key={s.slot} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Box component="label" htmlFor={`lc-${s.slot}`} sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{s.rotulo}</Box>
                      <Box component="select" id={`lc-${s.slot}`} value={v.fonte || ''} sx={campo}
                        onChange={(e) => setVariaveis((x) => ({ ...x, [s.slot]: { fonte: e.target.value, valor: x[s.slot]?.valor } }))}>
                        <option value="">Escolha de onde vem</option>
                        {(base.fontes || []).map((f) => <option key={f.chave} value={f.chave}>{f.rotulo}</option>)}
                      </Box>
                      {v.fonte === 'fixo' && (
                        <Box component="input" value={v.valor || ''} maxLength={200} placeholder="Texto que vai sempre igual"
                          onChange={(e) => setVariaveis((x) => ({ ...x, [s.slot]: { fonte: 'fixo', valor: e.target.value } }))} sx={campo} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {textoPrevia && (
              <Box>
                <Box component="span" sx={titulo}>Como o cliente vai ler</Box>
                <Box sx={{ p: '10px 12px', borderRadius: '10px 10px 10px 2px', bgcolor: 'var(--ch-inset)', border: '1px solid var(--ch-bdr)',
                  fontSize: '0.78rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {textoPrevia}
                  {campos.botoes.length > 0 && (
                    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '8px' }}>
                      {campos.botoes.map((b) => (
                        <Box key={b} component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--ch-blue, var(--ch-green-d))', border: '1px solid var(--ch-bdr2)', borderRadius: '6px', p: '4px 10px' }}>{b}</Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Box sx={{ ...ajuda, mt: '4px' }}>Prévia com dados de exemplo.</Box>
              </Box>
            )}

            <Box>
              <Box component="span" sx={titulo}>Quando sai</Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(base.opcoes_antecedencias || []).map((o) => {
                  const on = antecedencias.includes(o.minutos);
                  return (
                    <Box key={o.minutos} component="button" type="button" role="checkbox" aria-checked={on} onClick={() => trocarAntecedencia(o.minutos)}
                      sx={{ ...botaoBase, ...toque, p: '6px 10px', borderRadius: '6px', border: '1px solid', fontSize: '0.72rem', fontWeight: 700,
                        color: on ? 'var(--ch-green-d)' : 'var(--ch-text2)', bgcolor: on ? 'var(--ch-green-bg)' : 'transparent', borderColor: on ? 'var(--ch-green-b)' : 'var(--ch-bdr)' }}>
                      {o.rotulo}
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ ...ajuda, mt: '6px' }}>Até {base.max_antecedencias || 3}. Horário que já passou quando a {consulta} é marcada não é enviado.</Box>
            </Box>

            {template && campos.botoes.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box component="span" sx={titulo}>Botões de resposta</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '8px' }}>
                  <Box>
                    <Box component="label" htmlFor="lc-conf" sx={{ fontSize: '0.72rem', fontWeight: 600, display: 'block', mb: '4px' }}>Confirma</Box>
                    <Box component="select" id="lc-conf" value={confirmar} onChange={(e) => setConfirmar(e.target.value)} sx={campo}>
                      <option value="">Nenhum</option>
                      {campos.botoes.filter((b) => b !== remarcar).map((b) => <option key={b} value={b}>{b}</option>)}
                    </Box>
                  </Box>
                  <Box>
                    <Box component="label" htmlFor="lc-rem" sx={{ fontSize: '0.72rem', fontWeight: 600, display: 'block', mb: '4px' }}>Pede para remarcar</Box>
                    <Box component="select" id="lc-rem" value={remarcar} onChange={(e) => setRemarcar(e.target.value)} sx={campo}>
                      <option value="">Nenhum</option>
                      {campos.botoes.filter((b) => b !== confirmar).map((b) => <option key={b} value={b}>{b}</option>)}
                    </Box>
                  </Box>
                </Box>
                <Box sx={ajuda}>
                  Ao tocar em <b>{confirmar || 'Confirma'}</b>, a {consulta} fica confirmada sozinha (e o CRM junto).
                  {' '}<b>{remarcar || 'Remarcar'}</b> avisa no sino o responsável e quem marcou, com a {consulta} aberta na agenda — quem remarca é a pessoa.
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {erro && (
        <Box role="alert" sx={{ mx: '20px', mb: '10px', p: '8px 12px', borderRadius: '6px', bgcolor: 'var(--ch-red-bg)', border: '1px solid var(--ch-red-b)', color: 'var(--ch-red-d)', fontSize: '0.72rem', lineHeight: 1.45 }}>
          {erro}
        </Box>
      )}

      <Box sx={{ p: '14px 20px', pb: 'calc(14px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--ch-bdr)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <Box sx={{ flex: 1 }} />
        <Box component="button" type="button" onClick={() => !salvando && onFechar?.()}
          sx={{ ...botaoBase, ...toque, fontSize: '0.7rem', fontWeight: 600, color: 'var(--ch-text2)', bgcolor: 'transparent', border: '1px solid transparent', borderRadius: '6px', p: '7px 14px', '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' } }}>
          Cancelar
        </Box>
        <Box component="button" type="button" onClick={salvar} disabled={salvando || !base}
          sx={{ ...botaoBase, ...toque, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.02em', color: 'var(--ch-on-green)', bgcolor: 'var(--ch-green)', border: 'none', borderRadius: '6px', p: '7px 16px', '&:hover': { bgcolor: 'var(--ch-green-hover)' } }}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Box>
      </Box>
    </Drawer>
  );
}
